import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, ArrowRight, Search } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function CustomerBookShipment() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    origin_name: user?.name || "", origin_address: "", origin_city: "", origin_pincode: "", origin_phone: user?.phone || "",
    dest_name: "", dest_address: "", dest_city: "", dest_pincode: "", dest_phone: "",
    weight: "", pkg_type: "parcel", description: "",
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const calculatePrice = async () => {
    if (!form.weight || !form.origin_pincode || !form.dest_pincode) return;
    try {
      const res = await api.post("/pricing/calculate", {
        weight: parseFloat(form.weight),
        origin_pincode: form.origin_pincode,
        destination_pincode: form.dest_pincode,
      });
      setEstimatedPrice(res.data.price);
    } catch { setEstimatedPrice(null); }
  };

  const handleNext = async () => {
    if (step === 2) await calculatePrice();
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await api.post("/shipments", {
        origin: { name: form.origin_name, address: form.origin_address, city: form.origin_city, pincode: form.origin_pincode, phone: form.origin_phone },
        destination: { name: form.dest_name, address: form.dest_address, city: form.dest_city, pincode: form.dest_pincode, phone: form.dest_phone },
        package: { weight: parseFloat(form.weight), type: form.pkg_type, description: form.description }
      });
      setResult(res.data);
      setStep(4);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Book a Shipment</h1>
          <p className="text-slate-500 text-sm">Payment will be auto-deducted from your wallet.</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {["Sender", "Receiver", "Package", "Done"].map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? "bg-brand-500 text-white" : step === i + 1 ? "bg-brand-500 text-white ring-4 ring-brand-100" : "bg-slate-200 text-slate-500"}`}>
                  {step > i + 1 ? "✓" : i + 1}
                </div>
                <span className={`text-xs font-semibold mt-1 hidden sm:block ${step === i + 1 ? "text-brand-500" : "text-slate-400"}`}>{label}</span>
              </div>
              {i < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > i + 1 ? "bg-brand-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          {step === 1 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-5 uppercase">Sender Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: "origin_name", label: "Full Name", placeholder: "Sender's name" },
                  { name: "origin_phone", label: "Phone", placeholder: "+91 9876543210" },
                  { name: "origin_address", label: "Address", placeholder: "Street address", full: true },
                  { name: "origin_city", label: "City", placeholder: "Mumbai" },
                  { name: "origin_pincode", label: "PIN Code", placeholder: "400001" },
                ].map((f) => (
                  <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} data-testid={`book-${f.name}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-5 uppercase">Receiver Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: "dest_name", label: "Full Name", placeholder: "Receiver's name" },
                  { name: "dest_phone", label: "Phone", placeholder: "+91 9876543210" },
                  { name: "dest_address", label: "Address", placeholder: "Street address", full: true },
                  { name: "dest_city", label: "City", placeholder: "Delhi" },
                  { name: "dest_pincode", label: "PIN Code", placeholder: "110001" },
                ].map((f) => (
                  <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} data-testid={`book-${f.name}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-5 uppercase">Package Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Weight (kg)</label>
                  <input type="number" name="weight" value={form.weight} onChange={handleChange} step="0.1" min="0.1"
                    placeholder="e.g. 1.5" data-testid="book-weight"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Package Type</label>
                  <select name="pkg_type" value={form.pkg_type} onChange={handleChange} data-testid="book-pkg-type"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-white">
                    <option value="parcel">Parcel</option>
                    <option value="document">Document</option>
                    <option value="fragile">Fragile</option>
                    <option value="electronics">Electronics</option>
                    <option value="perishable">Perishable</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description (optional)</label>
                  <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Brief description..."
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 resize-none" />
                </div>
              </div>
              {estimatedPrice !== null && (
                <div className="mt-4 bg-brand-50 border border-brand-100 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-brand-600 uppercase">Estimated Price</p>
                    <p className="text-xs text-slate-500 mt-0.5">Your wallet: ₹{(user?.wallet_balance || 0).toFixed(2)}</p>
                  </div>
                  <span className="font-heading text-3xl font-bold text-brand-500">₹{estimatedPrice}</span>
                </div>
              )}
            </motion.div>
          )}

          {step === 4 && result && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">Booking Confirmed!</h2>
              <div className="bg-brand-50 rounded-lg p-5 mt-4 inline-block min-w-[200px]">
                <p className="text-xs text-brand-600 font-bold uppercase mb-1">Tracking ID</p>
                <p className="font-heading text-3xl font-bold text-brand-500">{result.tracking_id}</p>
                <p className="text-sm text-slate-500 mt-2">Price: ₹{result.price}</p>
              </div>
              <div className="flex gap-3 justify-center mt-6">
                <button onClick={() => navigate("/customer/shipments")}
                  className="px-5 py-2 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase">
                  View Shipments
                </button>
                <button onClick={() => { setStep(1); setResult(null); setEstimatedPrice(null); }}
                  className="px-5 py-2 border border-brand-500 text-brand-500 text-sm font-semibold rounded-sm hover:bg-brand-50 uppercase">
                  Book Another
                </button>
              </div>
            </motion.div>
          )}

          {step < 4 && (
            <div className="flex justify-between mt-8 pt-4 border-t border-slate-100">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50">Back</button>
              ) : <div />}
              {step < 3 ? (
                <button onClick={handleNext} data-testid={`book-next-${step}`}
                  className="px-6 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 flex items-center gap-2">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={loading} data-testid="book-confirm-btn"
                  className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-sm hover:bg-orange-600 disabled:opacity-70">
                  {loading ? "Booking..." : "Confirm Booking"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
